/**
 * CreateTab — the AI Workspace's Create options view (2026-08-25).
 *
 * Layout is Touch Up's, deliberately: the SAME shared classes (.controlsRow / .controlsLeft /
 * .dropdownRow / .generateGroup / .optionsBlock / .previewFloat / .promptTextarea), never forks of
 * them — controlsRowLayout.test.ts pins those. Style Grade and the board dialog share only
 * .controlsRow/.controlsLeft/.previewFloat (their generate control lives in .actionBar / their own
 * dialog-local CSS, not .dropdownRow/.generateGroup — corrected 2026-09-01, T3).
 *
 * What differs from Touch Up:
 *  - TWO pickers instead of one: task (Image / Video — no First/Last Shot Video, which needs a
 *    pair) and model. Model selection is SHARED with the Enhance tab (aiSelectedImageModel /
 *    aiSelectedVideoModel), not a third persisted field.
 *  - An "Add as new shot" checkbox under the prompt, hidden without the bridge because the ONLY
 *    shot-creation mechanism is Unity's insertShot verb.
 *  - No thumbnail requirement and no video-shot block: the run is text-only, so the selected shot
 *    is an anchor (and, unchecked, the target), not an input image.
 *  - runGenerateGate rather than Touch Up's inlined auth/credits fork — that duplication is a
 *    documented drift hazard (generateGate.ts).
 */

import { useState, useCallback, useMemo, useRef } from 'react';
import { AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { ShotGrid } from './ShotGrid.tsx';
import { WorkspaceShotPreview } from './WorkspaceShotPreview.tsx';
import { ImageFlyOverlay } from '@/components/ImageFlyOverlay.tsx';
import { DropdownPicker } from '@/components/DropdownPicker.tsx';
import { SparkleIcon } from '@/components/icons/SparkleIcon.tsx';
import { useProjectStore } from '@/store/projectStore.ts';
import { useCommentStore } from '@/store/commentStore.ts';
import { useMobileLayout } from '@/hooks/useMobileLayout.ts';
import { useAccountStore } from '@/store/accountStore.ts';
import { canAfford, getModelCost } from '@/ai/tokenCosts.ts';
import { capabilities } from '@/capabilities.ts';
import { runGenerateGate } from './generateGate.ts';
import { formatTimeEstimate } from '@/components/CostBanner.tsx';
import type { TimelineShot } from '@/types/timeline.ts';
import type { CatalogModel, ModelCategory } from '@/types/ai.ts';
import styles from './AiWorkspace.module.css';

const TIER_LABEL_KEYS = ['tierLabelFast', 'tierLabelQuality', 'tierLabelBest'] as const;

/** Image and Video only — 'continuous' needs two existing frames, which Create never has. */
const CREATE_TASKS = ['enhance', 'video'] as const;
const CREATE_TASK_LABEL_KEYS: Record<(typeof CREATE_TASKS)[number], string> = {
  enhance: 'taskLabelImage',
  video: 'taskLabelVideo',
};

interface Props {
  // Target shot (the anchor for "Add as new shot", the target when unchecked)
  effectiveCreateShot: TimelineShot | null;
  setCreateTargetShotId: (shotId: number | null) => void;

  // Prompt
  createPrompt: string;
  setCreatePrompt: (prompt: string) => void;

  // Task + model
  createTask: 'enhance' | 'video';
  setCreateTask: (task: 'enhance' | 'video') => void;
  aiSelectedImageModel: string;
  aiSelectedVideoModel: string;
  catalog: CatalogModel[];
  setSelectedModel: (category: ModelCategory, modelId: string) => void;

  // Options
  createAddAsNewShot: boolean;
  setCreateAddAsNewShot: (on: boolean) => void;

  // Actions
  handleCreateGenerate: () => void;
  closeWorkspace: () => void;
}

export function CreateTab({
  effectiveCreateShot,
  setCreateTargetShotId,
  createPrompt,
  setCreatePrompt,
  createTask,
  setCreateTask,
  aiSelectedImageModel,
  aiSelectedVideoModel,
  catalog,
  setSelectedModel,
  createAddAsNewShot,
  setCreateAddAsNewShot,
  handleCreateGenerate,
  closeWorkspace,
}: Props) {
  const { t } = useTranslation('aiWorkspace');
  const isMobile = useMobileLayout();

  // Dropdown state
  const [taskDropdownOpen, setTaskDropdownOpen] = useState(false);
  const toggleTaskDropdown = useCallback(() => setTaskDropdownOpen(v => !v), []);
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const toggleModelDropdown = useCallback(() => setModelDropdownOpen(v => !v), []);

  // Fullscreen preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewShotId, setPreviewShotId] = useState<number | null>(null);
  const previewSourceEl = useRef<HTMLElement | null>(null);
  const mainRef = useRef<HTMLDivElement>(null);

  // Project data for the grid
  const project = useProjectStore(s => s.project);
  const allScenes = project?.scenes ?? [];
  const separator = project?.documentSettings?.separator ?? '';
  const videoShots = useProjectStore(s => s.videoShots);
  const allShots = useMemo(
    () => allScenes.flatMap(s => s.shots),
    [allScenes],
  );
  const videoShotIds = useMemo(
    () => new Set(videoShots.map(v => v.shotId)),
    [videoShots],
  );
  const approvals = useCommentStore(s => s.approvals);
  const approvedShotIds = useMemo(
    () => new Set(approvals.filter(a => a.status === 'approved').map(a => a.shotId)),
    [approvals],
  );

  // Account state for auth / balance checks
  const accountUser = useAccountStore(s => s.accountUser);
  const accountBalance = useAccountStore(s => s.accountBalance);
  const setAuthDialogOpen = useAccountStore(s => s.setAuthDialogOpen);
  const standaloneAuthMode = useAccountStore(s => s.standaloneAuthMode);

  // Model list — same rules as the Enhance tab, so the two never show different catalogs.
  const category: ModelCategory = createTask === 'enhance' ? 'image' : 'video';
  const activeModelId = createTask === 'enhance' ? aiSelectedImageModel : aiSelectedVideoModel;
  const models = catalog
    .filter(m => m.category === category && m.id !== 'flux2-flash')
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const maxSeconds = Math.max(...models.map(m => m.estSeconds), 1);
  const selectedModel = models.find(m => m.id === activeModelId);
  const selectedIdx = models.findIndex(m => m.id === activeModelId);
  // The single-model case is spelled out so the trigger and the card below it cannot disagree
  // (a one-entry list is "Best", not "Fast") — the IMAGE arm only, as of the note below.
  // 20260921 - VIDEO MODELS CARRY THEIR OWN NAMES (StyleGradeTab.tsx's rule, adopted here): tier
  // words are image-only. This tab derived the trigger label from the model's INDEX for both
  // categories, so the two video models read "Fast 50 cr" / "Quality 100 cr" — Create was inventing
  // tier words for MiniMax H3 Max / H3 (2K), while Touch Up and Enhance show the catalog name. The
  // popover CARDS keep their tier words, exactly as Style Grade's do (the name is in their meta row).
  const selectedTier = category === 'video'
    ? (selectedModel?.name ?? 'Select')
    : models.length === 1
      ? t('tierLabelBest')
      : selectedIdx >= 0 && selectedIdx < TIER_LABEL_KEYS.length
        ? t(TIER_LABEL_KEYS[selectedIdx])
        : selectedModel?.name ?? 'Select';
  /**
   * 20260921 - "10cr/s" for per-second video models, "50 cr" for flat ones — lifted verbatim from
   * StyleGradeTab.tsx, which is also what WorkspaceMain and ModelPicker show. CreateTab was the ONE
   * picker in the app printing the bare `m.cost` for a video model, and that number is only the 5 s
   * reference price (tokenCosts.ts: h3-max cost 50 / costPerSecond 10), so it read as the whole
   * price of a run.
   */
  const priceLabel = (m: CatalogModel | undefined) =>
    m == null ? '' : m.costPerSecond != null ? `${m.costPerSecond}${t('creditSuffix')}/s` : `${m.cost} ${t('creditSuffix')}`;
  /**
   * 20260921 - THE QUOTE AND THE PRE-FLIGHT BOTH BILL BY DURATION (P3-18d).
   *
   * Single-shot run, so the button badge IS the per-model estimate — but for a video model the
   * charge is costPerSecond x clampDuration(shot.duration) (videoOrchestrator.generateSingleVideo
   * clamps the ANCHOR shot's duration and the server bills on it, "Add as new shot" included). The
   * old `selectedModel.cost` under-quoted a 12 s shot by up to 3x AND fed `insufficient` below, so a
   * user with 60 credits walked past this tab's own affordability gate and was rejected server-side.
   * getModelCost ignores the duration for flat models, so the image arm is byte-for-byte unchanged.
   */
  const perShotCost = selectedModel
    ? getModelCost(selectedModel.id, catalog, effectiveCreateShot?.duration)
    : 0;
  const perShotSeconds = selectedModel?.estSeconds ?? 0;

  const needsAuth = !accountUser;
  const insufficient = !!(accountBalance && !accountBalance.isBeta && !canAfford(accountBalance, perShotCost));

  // Highlight the selected target in the grid
  const selectedIds = useMemo(
    () => effectiveCreateShot ? new Set([effectiveCreateShot.id]) : new Set<number>(),
    [effectiveCreateShot],
  );

  // No thumbnail requirement and no video-shot block — the shot's picture is not an input here.
  const generateDisabled = !effectiveCreateShot || !createPrompt.trim();

  const handleGenerate = useCallback(() => {
    if (generateDisabled) return;
    handleCreateGenerate();
  }, [generateDisabled, handleCreateGenerate]);

  // Grid tap: pick the target shot; tapping the current target deselects it.
  const handleGridTap = useCallback((shotId: number) => {
    setCreateTargetShotId(shotId === effectiveCreateShot?.id ? null : shotId);
  }, [effectiveCreateShot, setCreateTargetShotId]);

  // Grid preview: long-press / double-click opens fullscreen
  const handleGridPreview = useCallback((shotId: number, cellEl: HTMLElement) => {
    previewSourceEl.current = cellEl;
    setPreviewShotId(shotId);
    setPreviewOpen(true);
  }, []);

  // ImageFlyOverlay items
  const previewItems = useMemo(
    () => allShots.filter(s => s.thumbnailDataUrl).map(s => ({ url: s.thumbnailDataUrl! })),
    [allShots],
  );
  const shotsWithThumbs = useMemo(
    () => allShots.filter(s => s.thumbnailDataUrl),
    [allShots],
  );
  const previewActiveIndex = useMemo(() => {
    if (previewShotId == null) return 0;
    const idx = shotsWithThumbs.findIndex(s => s.id === previewShotId);
    return idx >= 0 ? idx : 0;
  }, [previewShotId, shotsWithThumbs]);

  const handlePreviewIndexChange = useCallback((newIndex: number) => {
    const shot = shotsWithThumbs[newIndex];
    if (shot) setPreviewShotId(shot.id);
  }, [shotsWithThumbs]);

  return (
    <div ref={mainRef} className={styles.main} style={{ gap: 8 }}>
      {/* Controls + Preview row */}
      <div className={styles.controlsRow}>
      {/* Left column: dropdowns + prompt */}
      <div className={styles.controlsLeft}>
      {/* Dropdown row: Task + Model + Create button */}
      <div className={styles.dropdownRow}>
        {/* Task dropdown — Image / Video */}
        <div>
          {/* A noun naming the field, not the surface's verb: the verb already sits on the generate
              button a few inches right (createGenerate), and heading the picker with a second copy of
              it read as a second button. Shared with the Enhance tab's identical picker, exactly as
              sectionLabelModel is shared (20260827). */}
          <div className={styles.sectionLabel}>{t('sectionLabelType')}</div>
          <DropdownPicker
            selectedLabel={t(CREATE_TASK_LABEL_KEYS[createTask])}
            open={taskDropdownOpen}
            onToggle={toggleTaskDropdown}
          >
            <div className={styles.pickerList}>
              {CREATE_TASKS.map(tk => (
                <button
                  key={tk}
                  type="button"
                  data-testid={`create-task-${tk}`}
                  className={`${styles.pickerItem} ${tk === createTask ? styles.pickerItemSelected : ''}`}
                  onClick={() => {
                    setCreateTask(tk);
                    setTaskDropdownOpen(false);
                  }}
                >
                  {t(CREATE_TASK_LABEL_KEYS[tk])}
                </button>
              ))}
            </div>
          </DropdownPicker>
        </div>

        {/* Model dropdown */}
        <div>
          <div className={styles.sectionLabel}>{t('sectionLabelModel')}</div>
          <DropdownPicker
            selectedLabel={selectedTier}
            selectedSublabel={selectedModel ? priceLabel(selectedModel) : undefined}
            open={modelDropdownOpen}
            onToggle={toggleModelDropdown}
          >
            <div className={styles.tierCards}>
              {models.map((m, i) => {
                const selected = m.id === activeModelId;
                const tierLabel = models.length === 1
                  ? t('tierLabelBest')
                  : (i < TIER_LABEL_KEYS.length ? t(TIER_LABEL_KEYS[i]) : m.name);
                const speedPct = Math.round(((maxSeconds - m.estSeconds) / maxSeconds) * 100 + 20);

                return (
                  <button
                    key={m.id}
                    className={`${styles.tierCard} ${selected ? styles.tierCardSelected : ''}`}
                    onClick={() => {
                      setSelectedModel(category, m.id);
                      setModelDropdownOpen(false);
                    }}
                  >
                    <div className={styles.tierHeader}>
                      <span className={styles.tierLabel}>{tierLabel}</span>
                      <span className={styles.tierCost}>{priceLabel(m)}</span>
                    </div>
                    <div className={styles.tierSpeed}>
                      <div className={styles.tierSpeedFill} style={{ width: `${Math.min(100, speedPct)}%` }} />
                    </div>
                    <div className={styles.tierMeta}>
                      <span>{m.name}</span>
                      <span>~{m.estSeconds}s</span>
                      {m.resolution && <span className={styles.tierBadge}>{m.resolution}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </DropdownPicker>
        </div>

        {/* Generate / Auth button + credit line.
            .generateGroupStacked is Create-only and portrait-only (the rules live inside the phone
            MQ + its .force-mobile mirror): Create's row is one picker wider than Touch Up's, so in
            a narrow column this group wraps onto its own line, where .generateGroup's 14px top
            offset and 30px ::before hairline — both there to seat the button BESIDE a labelled
            picker — become dead space and an orphaned rule. Touch Up keeps the plain
            .generateGroup: its row still fits on one line and that shared-line hairline is the
            look this whole pass is matching. Desktop Create is unchanged. */}
        <div className={`${styles.generateGroup} ${styles.generateGroupStacked}`}>
          {needsAuth && (!capabilities.bridge || standaloneAuthMode) ? (
            <button className={styles.authBtn} onClick={() => { closeWorkspace(); setAuthDialogOpen(true); }}>
              <SparkleIcon />
              {t('signInToCreate')}
            </button>
          ) : !effectiveCreateShot ? (
            // 20260921 - THE DISABLED BUTTON SAYS WHY (P3-18c), using StyleGradeTab's own recipe
            // (its needsSelection arm swaps the label for selectShotsToEnhance and drops the credit
            // badge). Create's landing is DESIGNED to arrive with no target — the owner's T1+T2
            // ruling is that it "must not invent" one — so a greyed "Create ~12cr" with no stated
            // reason is the DEFAULT first thing a user sees on this tab. Same disabled control and
            // the same data-testid, so the gate itself is unchanged; only the copy appears.
            // touchUpNoShot ("Select a shot first") is the existing string for exactly this state
            // and already ships in all 11 locales; its `location` metadata still names the retired
            // TouchUpPanel.tsx, which is a docs residual for the localization pass, not a new key.
            <button className={styles.generateBtn} data-testid="create-generate" disabled>
              <SparkleIcon />
              {t('touchUpNoShot')}
            </button>
          ) : (
            <button
              className={styles.generateBtn}
              data-testid="create-generate"
              onClick={async () => {
                const gated = await runGenerateGate({ needsAuth, insufficient, onCloseHost: closeWorkspace });
                if (gated) return;
                handleGenerate();
              }}
              disabled={generateDisabled}
            >
              <SparkleIcon />
              {t('createGenerate')}
              <span className={styles.creditBadge}>
                ~{perShotCost}cr · {formatTimeEstimate(perShotSeconds)}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Prompt section */}
      <div className={styles.optionsBlock}>
        {/* 20260921 - THE PROMPT COPY FOLLOWS THE TASK (P3-18d). Both strings were image-only
            ("Describe the image you want to create" / "A wide angle panorama of the NY skyline"),
            and the section label renders uppercase, so a user in Video mode was told in capitals to
            describe an image. Two new keys rather than one medium-agnostic re-word: the image
            wording is the shipped, translated copy for the tab's default task, and re-wording it
            would churn 11 locales for the arm that was already right. */}
        <div className={styles.sectionLabel} style={{ marginTop: 8 }}>
          {t(createTask === 'video' ? 'createInstructionVideo' : 'createInstruction')}
        </div>
        <textarea
          className={styles.promptTextarea}
          value={createPrompt}
          onChange={e => setCreatePrompt(e.target.value)}
          placeholder={t(createTask === 'video' ? 'createPromptPlaceholderVideo' : 'createPromptPlaceholder')}
          rows={3}
          style={{ maxWidth: 600 }}
        />
        {/* Creating a shot is a Unity-only mutation (insertShot), so the option does not exist
            outside embedded mode rather than existing and failing. */}
        {capabilities.bridge && (
          <label className={styles.checkboxRow} style={{ marginTop: 6 }}>
            <input
              type="checkbox"
              data-testid="create-add-as-new-shot"
              checked={createAddAsNewShot}
              onChange={e => setCreateAddAsNewShot(e.target.checked)}
            />
            {t('addAsNewShot')}
          </label>
        )}
      </div>
      </div>{/* end controlsLeft */}

      {/* Preview thumbnail — fills remaining space, shrinks to fit */}
      <div className={styles.previewFloat}>
        <WorkspaceShotPreview shot={effectiveCreateShot} />
      </div>
      </div>{/* end controlsRow */}

      {/* Divider */}
      <div className={styles.gridDivider} />

      {/* Shot Grid */}
      <ShotGrid
        shots={allShots}
        scenes={allScenes}
        separator={separator}
        selectedIds={selectedIds}
        videoShotIds={videoShotIds}
        approvedShotIds={approvedShotIds}
        onTap={handleGridTap}
        onPreview={handleGridPreview}
        {...(isMobile ? { scrollParent: mainRef.current } : {})}
        /* Land on the selected shot when entering the tab, same as Enhance (owner rule
           2026-09-01, item E). Always handed over: ShotGrid is what withholds the scroll on
           phone/compact layouts, so the rule lives in one place for all four tabs. */
        initialScrollToId={effectiveCreateShot?.id ?? null}
      />

      {/* Fullscreen Preview Overlay */}
      <AnimatePresence>
        {previewOpen && previewItems.length > 0 && (
          <ImageFlyOverlay
            key="create-preview"
            activeIndex={previewActiveIndex}
            items={previewItems}
            sourceElement={previewSourceEl.current}
            onClose={() => setPreviewOpen(false)}
            onIndexChange={handlePreviewIndexChange}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
